import { observer } from 'mobx-react-lite'
import { useTranslationTyped } from 'i18n/i18n'
import { useRepository } from 'hooks/auth'
import { type ChatController } from '@breakoutlearning/firebase-repository/cubits/meeting/ChatController'
import classNames from 'classnames'

export const ChatNudgeMessage = observer(function ChatNudgeMessage({
  message,
  className,
  includeDate = true,
}: {
  message: ChatController['messages'][number]
  className?: string
  includeDate?: boolean
}) {
  const { tt } = useTranslationTyped()
  const repository = useRepository()

  // Only render if this is a nudge message with appropriate payload
  if (message.type !== 'nudge' || !message.metadata) return null

  const { metadata, nudgeSent } = message
  const userIds =
    'userIds' in metadata && Array.isArray(metadata.userIds)
      ? metadata.userIds
      : []

  if (!userIds.length || nudgeSent !== true) return null

  const nudgedUsers = userIds
    .map((userId) => repository.userStore.getUser(userId))
    .filter((user) => user.isLoaded)

  if (!nudgedUsers.length) return null

  const sent = message.createdAt ?? message.updatedAt

  // Format user names with 'and' before the last name
  const formatUserNames = (users: typeof nudgedUsers) => {
    const names = users.map((user) => user.fullName)
    if (names.length === 1) {
      return names[0]
    } else if (names.length === 2) {
      return `${names[0]} ${tt.common.and_lowercase()} ${names[1]}`
    } else {
      const allButLast = names.slice(0, -1).join(', ')
      const last = names[names.length - 1]
      return `${allButLast} ${tt.common.and_lowercase()} ${last}`
    }
  }

  const userNames = formatUserNames(nudgedUsers)

  // Format timestamp
  const formattedTime = sent?.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <div
      className={classNames(
        'flex flex-col items-center rounded-xl px-4 py-3',
        className
      )}
    >
      <div className="text-center text-body-medium font-light text-core-error">
        {userNames}
      </div>
      <div className="text-center text-body-medium font-bold text-core-error">
        {tt.meeting.alerted_to_join_meeting()}
      </div>
      {formattedTime && includeDate && (
        <div className="text-center text-body-small font-light text-on-surface-var">
          {formattedTime}
        </div>
      )}
    </div>
  )
})
